/**
 * Safe wrappers around localStorage.
 * Some in-app browsers (Instagram/Facebook WebView on iOS with "Prevent
 * Cross-Site Tracking" on, Safari private mode, some enterprise MDMs) throw
 * a SecurityError or QuotaExceededError on any localStorage access. If that
 * throws inside axios interceptors at boot, the whole app crashes and the
 * user sees a black screen. These wrappers swallow the throw.
 */

let memoryStore = {};

function hasLocalStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const testKey = "__nexus_probe__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

const LS_OK = hasLocalStorage();

export function safeGet(key) {
  if (!LS_OK) return memoryStore[key] || null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memoryStore[key] || null;
  }
}

export function safeSet(key, value) {
  if (LS_OK) {
    try {
      window.localStorage.setItem(key, value);
      return;
    } catch {
      // fall through to memory
    }
  }
  memoryStore[key] = value;
}

export function safeRemove(key) {
  if (LS_OK) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
  delete memoryStore[key];
}

/** True if we're inside Instagram/Facebook in-app browser. */
export function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Instagram|FBAN|FBAV|FB_IAB|Line\/|Twitter/i.test(ua);
}
